import type {MergeBoxPageData} from '../page-data/payloads/merge-box'
import {isFailingOrIncompleteStatus, type StatusChecksPageData} from '../page-data/payloads/status-checks'
import {getReviewRuleRollupMetadata} from './json-api-helpers'
import {mergeabilityStatus, Status} from './mergeability-status'

const tooltipForStatus = {
  [Status.Mergeable]: 'Ready to merge when you are.',
  [Status.NonRequiredChecksUnsuccessful]: 'Some optional checks are failing, but merging is still available.',
  [Status.Draft]: 'Still taking shape. Draft pull requests cannot be merged.',
  [Status.InMergeQueue]: 'Queued for merge.',
  [Status.ChecksPending]: 'Waiting for checks to finish.',
  [Status.ChecksFailing]: 'Checks are failing and need attention.',
  [Status.AwaitingReview]: "Waiting for a reviewer's approval.",
  [Status.ChangesRequested]: 'Reviewers have requested changes.',
  [Status.MergeConflicts]: 'Merge conflicts need to be resolved.',
  [Status.NonactionableFailure]: 'Merging is currently blocked.',
  [Status.UnableToMerge]: 'Merging is currently blocked.',
  [Status.Merged]: 'This pull request has been merged.',
  [Status.Closed]: 'This pull request has been closed.',
  [Status.Unknown]: '',
} satisfies Record<Status, string>

export function getStatus(
  isLoading?: boolean,
  pullRequest?: MergeBoxPageData['pullRequest'],
  mergeRequirements?: MergeBoxPageData['mergeRequirements'],
  statusChecksData?: StatusChecksPageData,
) {
  if (isLoading || !pullRequest) return null

  const requirements = mergeRequirements ?? null
  const status = mergeabilityStatus({pullRequest, mergeRequirements: requirements, statusChecksData})

  if (status !== Status.NonRequiredChecksUnsuccessful) return status
  if (!requirements) return Status.Unknown
  if (requirements.state === 'UNKNOWN' || requirements.state === 'UNMERGEABLE') {
    return mergeabilityStatus({pullRequest, mergeRequirements: requirements})
  }

  return status
}

function getAwaitingReviewTooltipDescription(mergeRequirements?: MergeBoxPageData['mergeRequirements']) {
  const reviewRules = getReviewRuleRollupMetadata(mergeRequirements ?? null)
  const failureReasons = reviewRules.flatMap(rule => rule.failureReasons)
  if (failureReasons.includes('THREAD_RESOLUTION_REQUIRED')) {
    return failureReasons.every(reason => reason === 'THREAD_RESOLUTION_REQUIRED')
      ? 'Some required conversations still need resolution.'
      : 'Some review requirements still need attention.'
  }
  if (
    !reviewRules.some(rule => rule.requiredReviewers > 0 || rule.requiresCodeowners || rule.failureReasons.length > 0)
  ) {
    return 'Some review requirements still need attention.'
  }

  return tooltipForStatus[Status.AwaitingReview]
}

function getFailingChecksTooltipDescription(statusChecksData: StatusChecksPageData) {
  const failingChecks = statusChecksData.statusRollup.summary.reduce(
    (count, check) => (isFailingOrIncompleteStatus(check.state) ? count + check.count : count),
    0,
  )
  if (failingChecks > 0) {
    return failingChecks === 1
      ? '1 check is failing and needs attention.'
      : `${failingChecks} checks are failing and need attention.`
  }

  return tooltipForStatus[Status.ChecksFailing]
}

function getOptionalChecksTooltipDescription(
  statusChecksData: StatusChecksPageData,
  mergeRequirements?: MergeBoxPageData['mergeRequirements'],
) {
  const mergeabilityConfirmed = mergeRequirements?.state === 'MERGEABLE'
  const hasFailedOptionalChecks = statusChecksData.statusChecks.some(
    check => !check.isRequired && isFailingOrIncompleteStatus(check.state),
  )
  if (!hasFailedOptionalChecks && statusChecksData.statusRollup.combinedState === 'PENDING_APPROVAL') {
    return mergeabilityConfirmed
      ? 'Ready to merge. Workflow approval is still pending.'
      : 'Workflow approval is still pending.'
  }
  if (!hasFailedOptionalChecks) {
    return mergeabilityConfirmed
      ? 'Ready to merge. Some optional checks have not passed.'
      : 'Some optional checks have not passed.'
  }
  if (!mergeabilityConfirmed) {
    return 'Some optional checks are failing.'
  }

  return tooltipForStatus[Status.NonRequiredChecksUnsuccessful]
}

export function getTooltipDescription(
  status: Status | null,
  statusChecksData?: StatusChecksPageData,
  mergeRequirements?: MergeBoxPageData['mergeRequirements'],
) {
  let tooltipDescription = status ? tooltipForStatus[status] : undefined

  if (status === Status.AwaitingReview) {
    tooltipDescription = getAwaitingReviewTooltipDescription(mergeRequirements)
  } else if (status === Status.ChecksFailing && statusChecksData) {
    tooltipDescription = getFailingChecksTooltipDescription(statusChecksData)
  } else if (status === Status.NonRequiredChecksUnsuccessful && statusChecksData) {
    tooltipDescription = getOptionalChecksTooltipDescription(statusChecksData, mergeRequirements)
  }

  if (!tooltipDescription) return
  return `${tooltipDescription} Click to view details in the merge panel.`
}
